import React from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import SearchableSelect from "../ui/SearchableSelect";
import { DollarSign, Users, Handshake, Trash2, Plus, Loader2, ChevronDown, ChevronRight, Calendar } from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";
import { DATE_FILTER_OPTIONS } from "../../utils/dateUtils";

const SettlementsReportTab = ({
  settlementsData, dateFilter, selectedSettleTech, setSelectedSettleTech,
  expandedWeeks, setExpandedWeeks,
  quickTech, setQuickTech, quickAmount, setQuickAmount,
  quickSign, setQuickSign, quickNotes, setQuickNotes, quickLoading,
  fetchReportData, formatCurrency, formatDate
}) => {
  const { isDark } = useTheme();
  if (!settlementsData) return null;

  const { weeks, summary, technicians: settleTechs } = settlementsData;

  const toggleWeek = (weekKey) => {
    setExpandedWeeks(prev => ({ ...prev, [weekKey]: !prev[weekKey] }));
  };

  const getAuthConfig = () => {
    const token = localStorage.getItem("token");
    return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  };

  const submitQuickSettle = async () => {
    if (!quickTech || !quickAmount || parseFloat(quickAmount) === 0) return;
    try {
      const finalAmount = quickSign === "negative" ? -Math.abs(parseFloat(quickAmount)) : Math.abs(parseFloat(quickAmount));
      await axios.post(`${API}/technicians/${quickTech}/settlements`, { amount: finalAmount, notes: quickNotes }, getAuthConfig());
      toast.success("Settlement recorded");
      setQuickAmount(""); setQuickNotes("");
      fetchReportData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record settlement");
    }
  };

  const deleteSettlementFromList = async (settlementId) => {
    try {
      await axios.delete(`${API}/technicians/settlements/${settlementId}`, getAuthConfig());
      toast.success("Settlement deleted");
      fetchReportData();
    } catch {
      toast.error("Failed to delete settlement");
    }
  };

  return (
    <div className="space-y-6">
      {/* Tech Filter */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-muted-foreground" />
          <span className="text-muted-foreground text-sm">Technician:</span>
        </div>
        <SearchableSelect
          value={selectedSettleTech}
          onChange={setSelectedSettleTech}
          options={[
            { value: "all", label: "All Technicians" },
            ...(settleTechs?.map(t => ({ value: t.member_id, label: t.name })) || []),
          ]}
          placeholder="All Technicians"
          searchThreshold={4}
          className="w-[200px]"
          triggerClassName="bg-theme-surface border-theme text-foreground h-10"
          data-testid="settle-tech-filter"
        />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="glass-card border-theme">
          <CardContent className="p-4">
            <div className="text-muted-foreground text-xs mb-1">Total Settled</div>
            <div className="text-xl font-bold text-cyan-500" data-testid="settle-summary-total">{formatCurrency(summary.total_settled)}</div>
            <div className="text-muted-foreground text-xs mt-1">{summary.settlement_count} settlement{summary.settlement_count !== 1 ? 's' : ''}</div>
          </CardContent>
        </Card>
        <Card className="glass-card border-theme">
          <CardContent className="p-4">
            <div className="text-muted-foreground text-xs mb-1">Adjustments</div>
            <div className={`text-xl font-bold ${summary.total_adjustments >= 0 ? 'text-green-500' : 'text-red-500'}`} data-testid="settle-summary-adj">
              {summary.total_adjustments >= 0 ? '+' : ''}{formatCurrency(summary.total_adjustments)}
            </div>
            <div className="text-muted-foreground text-xs mt-1">{summary.adjustment_count} adjustment{summary.adjustment_count !== 1 ? 's' : ''}</div>
          </CardContent>
        </Card>
        <Card className="glass-card border-theme">
          <CardContent className="p-4">
            <div className="text-muted-foreground text-xs mb-1">Weeks</div>
            <div className="text-xl font-bold text-foreground">{weeks.length}</div>
            <div className="text-muted-foreground text-xs mt-1">with settlements</div>
          </CardContent>
        </Card>
        <Card className="glass-card border-theme">
          <CardContent className="p-4">
            <div className="text-muted-foreground text-xs mb-1">Period</div>
            <div className="text-sm font-bold text-foreground">{DATE_FILTER_OPTIONS.find(o => o.value === dateFilter)?.label || dateFilter}</div>
            <div className="text-muted-foreground text-xs mt-1">Mon - Sun cycles</div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Add Settlement */}
      <Card className="glass-card border-theme">
        <CardHeader className="pb-3">
          <CardTitle className="text-foreground text-base font-['Manrope'] flex items-center gap-2">
            <Plus className="w-4 h-4 text-cyan-500" /> Record Settlement
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-3 items-end">
            <div className="flex-1 min-w-[160px]">
              <label className="text-xs text-muted-foreground">Technician</label>
              <Select value={quickTech} onValueChange={setQuickTech}>
                <SelectTrigger className="bg-theme-surface border-theme text-foreground mt-1" data-testid="quick-settle-tech">
                  <SelectValue placeholder="Select technician..." />
                </SelectTrigger>
                <SelectContent>
                  {settleTechs?.map(t => (
                    <SelectItem key={t.member_id} value={t.member_id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-[150px]">
              <label className="text-xs text-muted-foreground">Amount ($)</label>
              <div className="flex items-center gap-1 mt-1">
                <button
                  type="button"
                  onClick={() => setQuickSign(quickSign === "positive" ? "negative" : "positive")}
                  className={`flex-shrink-0 w-9 h-9 rounded-md border font-bold text-sm flex items-center justify-center transition-colors ${
                    quickSign === "positive"
                      ? "bg-red-500/15 border-red-500/30 text-red-500"
                      : "bg-green-500/15 border-green-500/30 text-green-500"
                  }`}
                  data-testid="quick-settle-sign"
                >
                  {quickSign === "positive" ? "+" : "-"}
                </button>
                <Input
                  type="number" min="0" step="0.01" inputMode="decimal"
                  value={quickAmount}
                  onChange={(e) => setQuickAmount(e.target.value.replace("-", ""))}
                  className="input-glass flex-1"
                  placeholder="0.00"
                  data-testid="quick-settle-amount"
                />
              </div>
            </div>
            <div className="flex-1 min-w-[140px]">
              <label className="text-xs text-muted-foreground">Notes</label>
              <Input
                value={quickNotes}
                onChange={(e) => setQuickNotes(e.target.value)}
                className="input-glass mt-1"
                placeholder="e.g., Weekly cash settle"
                data-testid="quick-settle-notes"
              />
            </div>
            <Button
              onClick={submitQuickSettle}
              disabled={quickLoading || !quickTech || !quickAmount || parseFloat(quickAmount) === 0}
              className="bg-cyan-600 hover:bg-cyan-700 text-white h-9 px-4"
              data-testid="quick-settle-submit"
            >
              {quickLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Handshake className="w-4 h-4 mr-1" />}
              Record
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            <span className="text-red-500 font-medium">+</span> Tech pays company &middot; <span className="text-green-500 font-medium">-</span> Company pays tech
          </p>
        </CardContent>
      </Card>

      {/* Weekly Breakdown */}
      {weeks.length === 0 ? (
        <Card className="glass-card border-theme">
          <CardContent className="py-12 text-center">
            <Handshake className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground">No settlements found for this period</p>
            <p className="text-muted-foreground text-sm mt-1">Use the form above to record a new settlement</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {weeks.map((week) => {
            const isExpanded = expandedWeeks[week.week_start] !== false;
            return (
              <Card key={week.week_start} className="glass-card border-theme overflow-hidden" data-testid={`week-${week.week_start}`}>
                <button
                  onClick={() => toggleWeek(week.week_start)}
                  className="w-full flex items-center justify-between p-4 hover:bg-theme-surface transition-colors"
                  data-testid={`week-toggle-${week.week_start}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-cyan-500/15 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-cyan-500" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-foreground font-semibold text-sm">{week.week_label}</h3>
                      <p className="text-muted-foreground text-xs">{week.count} settlement{week.count !== 1 ? 's' : ''} &middot; {week.by_technician.length} tech{week.by_technician.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`font-bold text-lg ${week.total_amount > 0 ? 'text-cyan-500' : week.total_amount < 0 ? 'text-green-500' : 'text-muted-foreground'}`}>
                      {week.total_amount > 0 ? '+' : ''}{formatCurrency(week.total_amount)}
                    </span>
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-theme">
                    <div className={`px-4 py-3 ${isDark ? 'bg-white/[0.02]' : 'bg-gray-50/50'}`}>
                      <div className="flex flex-wrap gap-3">
                        {week.by_technician.map((bt) => (
                          <div key={bt.technician_id} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm ${isDark ? 'bg-white/[0.03] border-white/10' : 'bg-white border-gray-200'}`}>
                            <span className="text-foreground font-medium">{bt.name}</span>
                            <span className={`font-semibold ${bt.total > 0 ? 'text-cyan-500' : bt.total < 0 ? 'text-green-500' : 'text-muted-foreground'}`}>
                              {bt.total > 0 ? '+' : ''}{formatCurrency(bt.total)}
                            </span>
                            <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 border-theme text-muted-foreground">{bt.count}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="divide-y divide-theme">
                      {week.settlements.map((s) => (
                        <div key={s.settlement_id} className="flex items-center justify-between px-4 py-3 hover:bg-theme-surface transition-colors" data-testid={`settlement-row-${s.settlement_id}`}>
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${s.amount > 0 ? 'bg-red-500/10' : 'bg-green-500/10'}`}>
                              <DollarSign className={`w-4 h-4 ${s.amount > 0 ? 'text-red-500' : 'text-green-500'}`} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-foreground font-medium text-sm">{s.technician_name}</span>
                                <span className={`font-semibold text-sm ${s.amount > 0 ? 'text-red-500' : 'text-green-500'}`}>
                                  {s.amount > 0 ? '+' : ''}{formatCurrency(s.amount)}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <span>{formatDate(s.created_at)}</span>
                                {s.notes && <span className="truncate max-w-[200px]">&middot; {s.notes}</span>}
                              </div>
                            </div>
                          </div>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-red-500 hover:text-red-400 hover:bg-red-500/10 flex-shrink-0"
                            onClick={(e) => { e.stopPropagation(); deleteSettlementFromList(s.settlement_id); }}
                            data-testid={`delete-settle-${s.settlement_id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SettlementsReportTab;
